import React, { useState, useMemo } from "react";
import {
  Search,
  User,
  Briefcase,
  Users,
  ChevronRight,
  ArrowUpRight,
  Calendar,
  Clock,
  Check,
} from "lucide-react";

interface RecommendOption {
  id: string;
  label: string;
  value: string;
  metadata?: {
    email?: string;
    role?: string;
    [key: string]: any;
  };
}

interface RecommendOptionsProps {
  message: string;
  clarificationType?:
    | "select_staff"
    | "select_project"
    | "select_task"
    | "select_client";
  clarificationField?: string; // The actual field name (e.g., "date", "hours", "project")
  clarificationFieldType?: "select" | "text" | "date"; // NEW -- tells frontend what input mode
  clarificationPlaceholder?: string; // NEW -- for text inputs
  multiSelect?: boolean; // NEW -- allow multiple selections
  options: RecommendOption[];
  onSelect: (option: RecommendOption) => void;
  isDark?: boolean;
  availableActions?: Array<{
    type: "manual_action";
    action: string;
    label: string;
    icon: string;
  }>;
  onManualAction?: (action: string) => void;
  // GenUI: Display metadata from backend - frontend should use this instead of hardcoded mappings
  display?: {
    icon?: string; // Icon name (e.g., "User", "Briefcase", "Calendar")
    title?: string; // Display title
    description?: string; // Additional description/help text
    renderHint?: string; // Hint for rendering (e.g., "card", "inline", "modal")
  };
  isCollapsed?: boolean; // NEW -- collapse to show only question text
  isOptionalPrompt?: boolean; // NEW -- indicates this is an optional fields prompt
}

export const RecommendOptions: React.FC<RecommendOptionsProps> = ({
  message,
  clarificationType,
  clarificationField,
  clarificationFieldType = "select", // Default to select for backwards compatibility
  clarificationPlaceholder,
  multiSelect = false,
  options,
  onSelect,
  isDark = false,
  availableActions = [],
  onManualAction,
  display, // GenUI: Display metadata from backend
  isCollapsed = false,
  isOptionalPrompt = false,
}) => {
  // 🔍 DEBUG: Log what we received
  console.log("[RecommendOptions] 📥 Received props:", {
    message,
    clarificationType,
    clarificationFieldType,
    clarificationPlaceholder,
    multiSelect,
    isOptionalPrompt,
    optionsCount: options?.length || 0,
    options: options,
    availableActionsCount: availableActions?.length || 0,
  });
  
  // Ensure clarificationFieldType is set correctly - if it's undefined and we have empty options, default to text
  const effectiveClarificationFieldType = clarificationFieldType || (options?.length === 0 ? "text" : "select");
  console.log("[RecommendOptions] Effective clarificationFieldType:", effectiveClarificationFieldType, "Original:", clarificationFieldType, "Options length:", options?.length);

  const [searchTerm, setSearchTerm] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [textInput, setTextInput] = useState(""); // For text input mode
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set()); // For multi-select mode

  // Filter out skip option for optional prompts (will be shown as separate button)
  const filteredOptionsForDisplay = useMemo(() => {
    const optionsToFilter = isOptionalPrompt 
      ? options.filter(opt => opt.id !== "__skip__")
      : options;
    
    if (!searchTerm.trim()) return optionsToFilter;

    const term = searchTerm.toLowerCase();
    return optionsToFilter.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        option.metadata?.email?.toLowerCase().includes(term) ||
        option.metadata?.role?.toLowerCase().includes(term),
    );
  }, [options, searchTerm, isOptionalPrompt]);

  // For backwards compatibility, keep filteredOptions as alias
  const filteredOptions = filteredOptionsForDisplay;

  // Show first 3 options by default, or all if showAll is true
  const displayOptions = showAll
    ? filteredOptions
    : filteredOptions.slice(0, 3);
  const hasMore = filteredOptions.length > 3 && !showAll;

  // Handle text input submission
  const handleTextSubmit = () => {
    if (!textInput.trim()) return;
    onSelect({
      id: "text_input",
      label: textInput.trim(),
      value: textInput.trim(),
    });
    setTextInput("");
  };

  // Handle multi-select toggle
  const handleMultiSelectToggle = (optionId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(optionId)) {
      newSelected.delete(optionId);
    } else {
      newSelected.add(optionId);
    }
    setSelectedIds(newSelected);
  };

  // Handle multi-select submission
  const handleMultiSelectSubmit = () => {
    if (selectedIds.size === 0) return;
    const selectedOptions = options.filter(opt => selectedIds.has(opt.id));
    onSelect({
      id: "multi_select",
      label: selectedOptions.map(opt => opt.label).join(", "),
      value: JSON.stringify(Array.from(selectedIds)),
    });
    setSelectedIds(new Set());
  };

  // GenUI: Icon component mapping - maps icon names from backend to React components
  const iconMap: Record<string, React.ReactNode> = {
    "User": <User className="w-4 h-4" />,
    "Briefcase": <Briefcase className="w-4 h-4" />,
    "ChevronRight": <ChevronRight className="w-4 h-4" />,
    "Users": <Users className="w-4 h-4" />,
    "Calendar": <Calendar className="w-4 h-4" />,
    "Clock": <Clock className="w-4 h-4" />,
    "HelpCircle": <Search className="w-4 h-4" />, // Fallback icon
  };

  // GenUI: Get icon from backend display metadata, with fallback to hardcoded mappings (for backward compatibility)
  const getIcon = () => {
    // Priority 1: Use display.icon from backend
    if (display?.icon && iconMap[display.icon]) {
      return iconMap[display.icon];
    }

    // Priority 2: Fallback to hardcoded mappings (backward compatibility)
    if (clarificationField) {
      switch (clarificationField) {
        case "staff":
          return <User className="w-4 h-4" />;
        case "project":
          return <Briefcase className="w-4 h-4" />;
        case "task":
          return <ChevronRight className="w-4 h-4" />;
        case "client":
          return <Users className="w-4 h-4" />;
        case "date":
          return <Calendar className="w-4 h-4" />;
        case "hours":
          return <Clock className="w-4 h-4" />;
        default:
          return null;
      }
    }

    // Priority 3: Fallback to clarificationType
    switch (clarificationType) {
      case "select_staff":
        return <User className="w-4 h-4" />;
      case "select_project":
        return <Briefcase className="w-4 h-4" />;
      case "select_task":
        return <ChevronRight className="w-4 h-4" />;
      case "select_client":
        return <Users className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // GenUI: Get title from backend display metadata, with fallback to hardcoded mappings (for backward compatibility)
  const getTitle = () => {
    // Priority 1: Use display.title from backend
    if (display?.title) {
      return display.title;
    }

    // Priority 2: Fallback to hardcoded mappings (backward compatibility)
    if (clarificationField) {
      switch (clarificationField) {
        case "staff":
          return "Select Staff Member";
        case "project":
          return "Select Project";
        case "task":
          return "Select Task";
        case "client":
          return "Select Client";
        case "date":
          return "Select Date";
        case "hours":
          return "Select Hours";
        default:
          return `Select ${clarificationField.charAt(0).toUpperCase() + clarificationField.slice(1)}`;
      }
    }

    // Priority 3: Fallback to clarificationType
    switch (clarificationType) {
      case "select_staff":
        return "Select Staff Member";
      case "select_project":
        return "Select Project";
      case "select_task":
        return "Select Task";
      case "select_client":
        return "Select Client";
      default:
        return "Select Option";
    }
  };

  return (
    <div
      className={`relative group rounded-lg border ${
        isDark ? "bg-gray-700 border-gray-600" : "bg-white border-gray-200"
      } p-4 ${isCollapsed ? "" : "space-y-3"}`}
    >
      {/* Quick action icon - Top right corner (only when not collapsed) */}
      {!isCollapsed && availableActions.length > 0 && onManualAction && (
        <button
          onClick={() => onManualAction(availableActions[0].action)}
          className={`
            absolute -top-2 -right-2 
            p-2 rounded-lg shadow-lg
            transition-all duration-200
            hover:scale-110
            z-10
            ${
              isDark
                ? "bg-accent-600 hover:bg-accent-700 text-white"
                : "bg-accent-500 hover:bg-accent-600 text-white"
            }
          `}
          title={availableActions[0].label}
        >
          <ArrowUpRight className="w-4 h-4" />
        </button>
      )}
      {/* Header */}
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 p-2 rounded ${
            isDark
              ? "bg-purple-900/50 text-purple-300"
              : "bg-purple-100 text-purple-600"
          }`}
        >
          {getIcon()}
        </div>
        <div className="flex-1">
          <h4
            className={`font-semibold text-sm mb-1 ${
              isDark ? "text-purple-200" : "text-gray-900"
            }`}
          >
            {getTitle()}
          </h4>
          <p
            className={`text-sm ${
              isDark ? "text-purple-300" : "text-gray-700"
            }`}
          >
            {/* GenUI: Use display.description if available, otherwise use message */}
            {display?.description || message}
          </p>
        </div>
      </div>

      {/* Text Input Mode - hide when collapsed */}
      {!isCollapsed && effectiveClarificationFieldType === "text" && (
        <div className="space-y-2">
          <input
            type="text"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleTextSubmit();
              }
            }}
            placeholder={clarificationPlaceholder || "Enter value..."}
            className={`w-full px-4 py-3 rounded-lg border text-sm ${
              isDark
                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-purple-500"
                : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-purple-500"
            } focus:outline-none focus:ring-2 focus:ring-purple-500/20`}
          />
          <button
            onClick={handleTextSubmit}
            disabled={!textInput.trim()}
            className={`w-full py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
              isDark
                ? textInput.trim()
                  ? "bg-purple-600 hover:bg-purple-700 text-white"
                  : "bg-gray-700 text-gray-500 cursor-not-allowed"
                : textInput.trim()
                  ? "bg-purple-600 hover:bg-purple-700 text-white"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            Submit
          </button>
        </div>
      )}

      {/* Select Mode (existing behavior) - hide when collapsed */}
      {!isCollapsed && effectiveClarificationFieldType === "select" && (
        <>
          {/* Search Input (if more than 5 options) */}
          {options.length > 5 && (
            <div className="relative">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className={`w-full pl-10 pr-3 py-2 rounded border text-sm ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-purple-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-purple-500"
                } focus:outline-none focus:ring-2 focus:ring-purple-500/20`}
              />
            </div>
          )}

          {/* Options Grid */}
          <div className="space-y-2">
            {displayOptions.length === 0 ? (
              <p
                className={`text-sm text-center py-4 ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              >
                No matches found
              </p>
            ) : (
              displayOptions.map((option) => {
                const isSelected = multiSelect && selectedIds.has(option.id);
                return (
                  <button
                    key={option.id}
                    onClick={() => {
                      console.log("[RecommendOptions] Option clicked:", {
                        optionId: option.id,
                        multiSelect,
                        action: multiSelect ? "toggle" : "select"
                      });
                      if (multiSelect) {
                        handleMultiSelectToggle(option.id);
                      } else {
                        onSelect(option);
                      }
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                      isDark
                        ? isSelected
                          ? "bg-purple-900/50 border-purple-500 text-white"
                          : "bg-gray-700 border-gray-600 hover:border-purple-500 hover:bg-gray-600 text-white"
                        : isSelected
                          ? "bg-purple-100 border-purple-400 text-gray-900"
                          : "bg-white border-gray-200 hover:border-purple-400 hover:bg-purple-50 text-gray-900"
                    } focus:outline-none focus:ring-2 focus:ring-purple-500/50`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        {multiSelect && (
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected
                                ? isDark
                                  ? "bg-purple-500 border-purple-500"
                                  : "bg-purple-600 border-purple-600"
                                : isDark
                                  ? "border-gray-500"
                                  : "border-gray-300"
                            }`}
                          >
                            {isSelected && (
                              <Check className={`w-3 h-3 ${isDark ? "text-white" : "text-white"}`} />
                            )}
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="font-medium text-sm">{option.label}</div>
                          {option.metadata?.email && (
                            <div
                              className={`text-xs mt-0.5 ${
                                isDark ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              {option.metadata.email}
                            </div>
                          )}
                          {option.metadata?.role &&
                            clarificationType === "select_staff" && (
                              <div
                                className={`text-xs mt-1 inline-block px-2 py-0.5 rounded ${
                                  isDark
                                    ? "bg-purple-900/50 text-purple-300"
                                    : "bg-purple-100 text-purple-700"
                                }`}
                              >
                                {option.metadata.role}
                              </div>
                            )}
                        </div>
                      </div>
                      {!multiSelect && (
                        <ChevronRight
                          className={`w-4 h-4 ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}
                        />
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Multi-select Action Buttons */}
          {multiSelect && (
            <div className="flex gap-2">
              <button
                onClick={handleMultiSelectSubmit}
                disabled={selectedIds.size === 0}
                className={`flex-1 py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
                  selectedIds.size > 0
                    ? isDark
                      ? "bg-purple-600 hover:bg-purple-700 text-white"
                      : "bg-purple-600 hover:bg-purple-700 text-white"
                    : isDark
                      ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {selectedIds.size > 0 ? `Done (${selectedIds.size} selected)` : "Select fields"}
              </button>
              {isOptionalPrompt && (
                <button
                  onClick={() => onSelect({ id: "__skip__", label: "Skip", value: "__skip__" })}
                  className={`py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-gray-300 border border-gray-600"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  Skip
                </button>
              )}
            </div>
          )}
        </>
      )}

      {/* Date Mode (uses select UI but with date-specific options) - hide when collapsed */}
      {!isCollapsed && clarificationFieldType === "date" && (
        <>
          {/* Search Input (if more than 5 options) */}
          {options.length > 5 && (
            <div className="relative">
              <Search
                className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className={`w-full pl-10 pr-3 py-2 rounded border text-sm ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-purple-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-purple-500"
                } focus:outline-none focus:ring-2 focus:ring-purple-500/20`}
              />
            </div>
          )}

          {/* Options Grid */}
          <div className="space-y-2">
            {displayOptions.length === 0 ? (
              <p
                className={`text-sm text-center py-4 ${
                  isDark ? "text-gray-400" : "text-gray-600"
                }`}
              >
                No matches found
              </p>
            ) : (
              displayOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => onSelect(option)}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                    isDark
                      ? "bg-gray-700 border-gray-600 hover:border-purple-500 hover:bg-gray-600 text-white"
                      : "bg-white border-gray-200 hover:border-purple-400 hover:bg-purple-50 text-gray-900"
                  } focus:outline-none focus:ring-2 focus:ring-purple-500/50`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{option.label}</div>
                    </div>
                    <ChevronRight
                      className={`w-4 h-4 ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    />
                  </div>
                </button>
              ))
            )}
          </div>
        </>
      )}

      {/* Show More Button - hide when collapsed */}
      {!isCollapsed && hasMore && (
        <button
          onClick={() => setShowAll(true)}
          className={`w-full py-2 text-sm font-medium rounded ${
            isDark
              ? "text-purple-300 hover:bg-purple-900/30"
              : "text-purple-600 hover:bg-purple-100"
          } transition-colors`}
        >
          Show {filteredOptions.length - 3} more options
        </button>
      )}

      {/* Collapse Button - hide when collapsed */}
      {!isCollapsed && showAll && filteredOptions.length > 3 && (
        <button
          onClick={() => setShowAll(false)}
          className={`w-full py-2 text-sm font-medium rounded ${
            isDark
              ? "text-purple-300 hover:bg-purple-900/30"
              : "text-purple-600 hover:bg-purple-100"
          } transition-colors`}
        >
          Show less
        </button>
      )}
    </div>
  );
};
